import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { analyticsAPI } from '../services/api';
import api, { subscribeToAnalyticsUpdates } from '../config/api';
import { useAuth } from '../context/AuthContext';
import {
  CHANNEL_COLORS,
  NEUTRAL_MARK,
  STATUS_COLORS,
  SURFACE,
  axisProps,
  gridProps,
  yAxisProps,
} from '../utils/chartTheme';
import {
  EmptyState,
  LoadingBlock,
  Meter,
  MeterRow,
  MetricGrid,
  MetricTile,
  PageHeader,
  RefreshButton,
  SeriesLabel,
  StatCard,
} from '../components/ui';
import {
  IconAIVoice,
  IconChart,
  IconDiagnosis,
  IconError,
  IconInfo,
  IconPatients,
  IconSMS,
  IconStar,
  IconSuccess,
  IconVoiceCall,
} from '../components/icons';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n, decimals = 0) => {
  if (n === null || n === undefined) return '—';
  if (typeof n === 'string') n = parseFloat(n);
  if (isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const pct = (n) => `${Number(n ?? 0)}%`;

const CHANNEL_LABELS = {
  sms: 'SMS',
  ivr: 'Voice SMS',
  voice: 'Voice SMS',
  elevenlabs_ivr: 'AI Voice',
  whatsapp: 'WhatsApp',
};

/**
 * Channel identity. Every chart, dot and meter looks colour up by channel key,
 * never by array index — so a channel keeps its colour even when another one
 * has no data and drops out of the render.
 */
const CHANNELS = [
  { key: 'SMS', color: CHANNEL_COLORS.sms },
  { key: 'Voice SMS', color: CHANNEL_COLORS.voiceSms },
  { key: 'AI Voice', color: CHANNEL_COLORS.aiVoice },
  { key: 'WhatsApp', color: CHANNEL_COLORS.whatsapp },
];
const StarRating = ({ rating, max = 5 }) => (
  <span className="flex items-center gap-0.5" aria-label={`${rating} out of ${max} stars`}>
    {Array.from({ length: max }).map((_, i) => (
      <IconStar
        key={i}
        size={13}
        weight={i < Math.round(rating) ? 'fill' : 'regular'}
        className={i < Math.round(rating) ? 'text-amber-500' : 'text-gray-300'}
        aria-hidden="true"
      />
    ))}
  </span>
);

// ─── Chart chrome ─────────────────────────────────────────────────────────────

const Legend = ({ items }) => (
  <ul className="flex flex-wrap items-center gap-x-4 gap-y-2">
    {items.map(({ key, color }) => (
      <li key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
        <span
          aria-hidden="true"
          className="h-2 w-2 flex-shrink-0 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {key}
      </li>
    ))}
  </ul>
);

const ChartTooltip = ({ active, payload, label, showTotal = false }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  return (
    <div className="min-w-[160px] rounded-lg border border-gray-200 bg-white p-3 shadow-lg shadow-gray-900/5">
      <p className="mb-2 text-xs font-semibold text-gray-900">{label}</p>
      <ul className="space-y-1">
        {payload.map((entry) => (
          <li key={entry.name} className="flex items-center justify-between gap-4 text-xs">
            <span className="flex items-center gap-1.5 text-gray-500">
              <span
                aria-hidden="true"
                className="h-2 w-2 flex-shrink-0 rounded-sm"
                style={{ backgroundColor: entry.color || entry.fill }}
              />
              {entry.name}
            </span>
            <span className="font-semibold tabular-nums text-gray-900">
              {Number(entry.value).toLocaleString()}
            </span>
          </li>
        ))}
      </ul>
      {showTotal && payload.length > 1 && (
        <div className="mt-2 flex items-center justify-between gap-4 border-t border-gray-100 pt-2 text-xs">
          <span className="text-gray-500">Total</span>
          <span className="font-semibold tabular-nums text-gray-900">
            {total.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Supabase direct fallback (when backend API is unavailable) ────────────────

const computeSupabaseFallback = (interactions, patientCount) => {
  // Channel buckets — keep mNotify and ElevenLabs strictly separate so
  // the per-vendor cards on the Analytics page don't double-count.
  const smsRows = interactions.filter(i => i.channel === 'sms');
  const mnotifyVoiceRows = interactions.filter(i => ['ivr', 'voice'].includes(i.channel));
  const elevenlabsRows = interactions.filter(i => i.channel === 'elevenlabs_ivr');
  // "All voice" is only used for the headline total + answer rate.
  const voiceRows = [...mnotifyVoiceRows, ...elevenlabsRows];
  const ivrRows = mnotifyVoiceRows; // legacy alias used downstream
  const answeredVoice = voiceRows.filter(i => i.call_answered);
  const answerRate = voiceRows.length > 0
    ? Math.round(answeredVoice.length / voiceRows.length * 1000) / 10
    : 0;
  const elevenlabsAnswered = elevenlabsRows.filter(i => i.call_answered).length;
  const elevenlabsAnswerRate = elevenlabsRows.length > 0
    ? Math.round(elevenlabsAnswered / elevenlabsRows.length * 1000) / 10
    : 0;

  // Channel performance
  const channelMap = {};
  interactions.forEach(i => {
    const ch = i.channel || 'sms';
    if (!channelMap[ch]) channelMap[ch] = { delivered: 0, engaged: 0 };
    channelMap[ch].delivered += 1;
    if (i.call_answered || ['delivered', 'read', 'answered'].includes(i.status)) {
      channelMap[ch].engaged += 1;
    }
  });
  const channelLabels = { sms: 'SMS', voice: 'Voice SMS', ivr: 'Voice SMS', whatsapp: 'WhatsApp', elevenlabs_ivr: 'AI Voice' };
  const channelPerf = [];
  const seenLabels = new Set();
  Object.entries(channelMap).forEach(([ch, counts]) => {
    const label = channelLabels[ch] || ch.toUpperCase();
    if (seenLabels.has(label)) {
      const existing = channelPerf.find(r => r.channel === label);
      if (existing) {
        existing.delivered += counts.delivered;
        existing.engaged += counts.engaged;
        existing.percentage = existing.delivered > 0
          ? Math.round(existing.engaged / existing.delivered * 1000) / 10 : 0;
      }
    } else {
      seenLabels.add(label);
      const p = counts.delivered > 0 ? Math.round(counts.engaged / counts.delivered * 1000) / 10 : 0;
      channelPerf.push({ channel: label, delivered: counts.delivered, engaged: counts.engaged, percentage: p });
    }
  });

  // Weekly engagement
  const now = new Date();
  const WEEKS = 4;
  const weekBuckets = {};
  for (let i = 1; i <= WEEKS; i++) {
    weekBuckets[i] = { ivr: 0, sms: 0, whatsapp: 0, voiceAI: 0, total: 0 };
  }
  interactions.forEach(item => {
    if (!item.created_at) return;
    const created = new Date(item.created_at);
    const daysAgo = Math.floor((now - created) / 86400000);
    let weekNum = WEEKS - Math.floor(daysAgo / 7);
    if (weekNum < 1) weekNum = 1;
    if (weekNum > WEEKS) return;
    const ch = item.channel || 'sms';
    const key = ch === 'elevenlabs_ivr' ? 'voiceAI'
      : ch === 'whatsapp' ? 'whatsapp'
      : ch === 'sms' ? 'sms' : 'ivr';
    weekBuckets[weekNum][key] = (weekBuckets[weekNum][key] || 0) + 1;
    weekBuckets[weekNum].total += 1;
  });
  const engagementWeeks = Object.entries(weekBuckets).map(([w, data]) => ({
    week: `Week ${w}`,
    value: data.total,
    IVR: data.ivr,
    SMS: data.sms,
    WhatsApp: data.whatsapp,
    voiceAI: data.voiceAI,
  }));

  // mNotify stats fallback
  const smsDelivered = smsRows.filter(i => ['delivered', 'read'].includes(i.status)).length;
  const smsFailed = smsRows.filter(i => i.status === 'failed').length;
  const ivrAnswered = ivrRows.filter(i => i.call_answered).length;

  return {
    summary: {
      total_patients: patientCount,
      active_patients: patientCount,
      total_campaigns: 0,
      active_campaigns: 0,
      total_voice_calls: voiceRows.length,
      total_answered_calls: answeredVoice.length,
      answer_rate_pct: answerRate,
      total_sms_sent: smsRows.length,
      avg_feedback_rating: 0,
      total_feedback_count: 0,
      // Per-vendor breakouts so the AI Voice card and the mNotify Voice
      // card render distinct numbers (previously both were stuck at 0
      // in the fallback path).
      elevenlabs_calls: elevenlabsRows.length,
      elevenlabs_answered: elevenlabsAnswered,
      elevenlabs_answer_rate_pct: elevenlabsAnswerRate,
      mnotify_ivr_calls: mnotifyVoiceRows.length,
      mnotify_ivr_answered: mnotifyVoiceRows.filter(i => i.call_answered).length,
      mnotify_answer_rate_pct: mnotifyVoiceRows.length > 0
        ? Math.round(mnotifyVoiceRows.filter(i => i.call_answered).length / mnotifyVoiceRows.length * 1000) / 10
        : 0,
    },
    channelPerf,
    engagementWeeks,
    mnotifyStats: {
      sms: {
        total_sent: smsRows.length,
        delivered: smsDelivered,
        failed: smsFailed,
        pending: smsRows.length - smsDelivered - smsFailed,
        delivery_rate_pct: smsRows.length > 0
          ? Math.round(smsDelivered / smsRows.length * 1000) / 10 : 0,
        total_campaigns: 0,
      },
      voice: {
        total_calls: ivrRows.length,
        answered: ivrAnswered,
        unanswered: ivrRows.length - ivrAnswered,
        answer_rate_pct: ivrRows.length > 0
          ? Math.round(ivrAnswered / ivrRows.length * 1000) / 10 : 0,
        avg_duration_seconds: 0,
        total_campaigns: 0,
      },
    },
  };
};

// ─── Analytics Component ───────────────────────────────────────────────────────

const Analytics = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('engagement');

  // Data state
  const [summary, setSummary] = useState(null);
  const [engagementData, setEngagementData] = useState([]);
  const [channelData, setChannelData] = useState([]);
  const [feedbackData, setFeedbackData] = useState(null);
  const [diagnosisData, setDiagnosisData] = useState([]);
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [mnotifyStats, setMnotifyStats] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [realtimeActive, setRealtimeActive] = useState(false);

  const subscriptionRef = useRef(null);
  const debounceRef = useRef(null);

  // ── Data Fetching ────────────────────────────────────────────────────────────

  const fetchAll = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    // Use allSettled so a single failing endpoint doesn't blank the entire page.
    // syncElevenLabs=true (first arg) tells the backend to reconcile any
    // pending AI Voice rows from the ElevenLabs API before computing the
    // summary — this is what makes the answer-rate KPI correct even when
    // the post-call webhook never fired.
    const userId = user?.id ?? null;
    const [summaryRes, engagementRes, channelRes, feedbackRes, mnotifyRes] = await Promise.allSettled([
      analyticsAPI.getSummary(true, userId),
      analyticsAPI.getWeeklyEngagement(4, userId),
      analyticsAPI.getChannelPerformance(userId),
      analyticsAPI.getFeedbackSummary(null, userId),
      analyticsAPI.getMNotifyStats(userId),
    ]);

    let anyBackendOk = false;

    if (summaryRes.status === 'fulfilled' && summaryRes.value?.data) {
      setSummary(summaryRes.value.data);
      anyBackendOk = true;
    }
    if (engagementRes.status === 'fulfilled' && engagementRes.value?.data) {
      setEngagementData(engagementRes.value.data);
      anyBackendOk = true;
    }
    if (channelRes.status === 'fulfilled' && channelRes.value?.data) {
      setChannelData(channelRes.value.data);
      anyBackendOk = true;
    }
    if (feedbackRes.status === 'fulfilled' && feedbackRes.value?.data) {
      setFeedbackData(feedbackRes.value.data);
      anyBackendOk = true;
    }
    if (mnotifyRes.status === 'fulfilled' && mnotifyRes.value?.data) {
      setMnotifyStats(mnotifyRes.value.data);
      anyBackendOk = true;
    }

    // If every backend call failed, fall back to direct Supabase queries.
    // Scope by user_id so we never accidentally surface another tenant's
    // rows when RLS is permissive, and bound the row scan so totals don't
    // silently truncate at PostgREST's default 1k row cap.
    if (!anyBackendOk && userId) {
      try {
        const ninetyDaysAgoISO = new Date(Date.now() - 90 * 86400000).toISOString();
        const [patientsRes, interactionsRes] = await Promise.allSettled([
          api.get('/patients', { params: { user_id: userId } }),
          api.get('/analytics/interactions', {
            params: { user_id: userId, since: ninetyDaysAgoISO, limit: 10000 },
          }),
        ]);

        const patientCount = patientsRes.status === 'fulfilled'
          ? (patientsRes.value.data?.patients?.length ?? 0) : 0;
        const interactions = interactionsRes.status === 'fulfilled'
          ? (interactionsRes.value.data?.interactions ?? []) : [];

        const fallback = computeSupabaseFallback(interactions, patientCount);
        setSummary(fallback.summary);
        if (fallback.channelPerf.length > 0) setChannelData(fallback.channelPerf);
        if (fallback.engagementWeeks.some(w => w.value > 0)) setEngagementData(fallback.engagementWeeks);
        setMnotifyStats(fallback.mnotifyStats);

        console.warn('[Analytics] Backend unavailable — using REST API fallback.');
      } catch (err) {
        console.error('[Analytics] REST API fallback also failed:', err);
        setError('Unable to load analytics data. Check your connection and API configuration.');
      }
    }

    setLastUpdated(new Date());
    setLoading(false);
    setRefreshing(false);
  }, [user?.id]);

  // ── Diagnosis Breakdown ──────────────────────────────────────────────────

  const fetchDiagnosisData = async () => {
    if (!user?.id) return;
    setDiagnosisLoading(true);
    try {
      const { data: resp } = await api.get('/patients', { params: { user_id: user.id } });
      const patients = resp?.patients ?? resp ?? [];
      const counts = {};
      patients.forEach((p) => {
        if (!p.diagnosis) return;
        const d = p.diagnosis || 'Unknown';
        counts[d] = (counts[d] || 0) + 1;
      });
      if (Object.keys(counts).length > 0) {
        const sorted = Object.entries(counts)
          .map(([diagnosis, count]) => ({ diagnosis, count }))
          .sort((a, b) => b.count - a.count);
        setDiagnosisData(sorted);
      }
    } catch (err) {
      console.error('[Analytics] Diagnosis fetch error:', err);
    } finally {
      setDiagnosisLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'diagnosis') {
      fetchDiagnosisData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, user?.id]);

  // ── Real-time Supabase Subscription ─────────────────────────────────────────

  useEffect(() => {
    fetchAll();

    // Subscribe to real-time updates
    try {
      subscriptionRef.current = subscribeToAnalyticsUpdates(({ table }) => {
        console.log('[Analytics] Real-time update:', table);
        // Debounced refresh: wait 2s before re-fetching to avoid hammering the backend
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchAll(true), 2000);
      });
      setRealtimeActive(true);
    } catch (err) {
      console.warn('Real-time subscription unavailable:', err);
      setRealtimeActive(false);
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (subscriptionRef.current) {
        try {
          subscriptionRef.current.unsubscribe();
        } catch (_) {}
      }
    };
  }, [fetchAll]);

  // ── Derived values ───────────────────────────────────────────────────────────

  const smsRate = mnotifyStats?.sms?.delivery_rate_pct ?? 0;
  const voiceRate = summary?.mnotify_answer_rate_pct ?? mnotifyStats?.voice?.answer_rate_pct ?? 0;
  const voiceCalls = summary?.mnotify_ivr_calls ?? mnotifyStats?.voice?.total_calls ?? 0;
  const voiceAnswered = summary?.mnotify_ivr_answered ?? mnotifyStats?.voice?.answered ?? 0;
  const aiRate = summary?.elevenlabs_answer_rate_pct ?? 0;
  const aiCalls = summary?.elevenlabs_calls ?? 0;
  const aiAnswered = summary?.elevenlabs_answered ?? 0;

  const stats = [
    {
      id: 'patients',
      label: 'Total Patients',
      value: summary ? fmt(summary.total_patients) : '—',
      icon: IconPatients,
      footnote: summary ? `${fmt(summary.active_patients)} active in the last 30 days` : '',
    },
    {
      id: 'sms_sent',
      label: 'SMS Sent',
      value: mnotifyStats
        ? fmt(mnotifyStats.sms?.total_sent)
        : summary
        ? fmt(summary.total_sms_sent)
        : '—',
      icon: IconSMS,
      meter: { value: smsRate, tone: 'accent' },
      footnote:
        mnotifyStats && mnotifyStats.sms?.total_sent > 0
          ? `${smsRate}% delivered`
          : 'No SMS sent yet',
    },
    {
      id: 'calls',
      label: 'Voice SMS Answer Rate',
      value: summary || mnotifyStats ? pct(voiceRate) : '—',
      icon: IconVoiceCall,
      meter: { value: voiceRate, tone: 'voice' },
      footnote: `${fmt(voiceAnswered)} of ${fmt(voiceCalls)} calls`,
    },
    {
      id: 'elevenlabs_answer_rate',
      label: 'AI Voice Answer Rate',
      value: summary ? pct(aiRate) : '—',
      icon: IconAIVoice,
      meter: { value: aiRate, tone: 'ai' },
      footnote: `${fmt(aiAnswered)} of ${fmt(aiCalls)} calls`,
    },
  ];

  const tabs = [
    { id: 'engagement', name: 'Engagement Trends' },
    { id: 'channel', name: 'Channel Performance' },
    { id: 'mnotify', name: 'SMS & Voice' },
    { id: 'feedback', name: 'Feedback' },
    { id: 'diagnosis', name: 'Diagnoses' },
  ];

  // Weekly engagement normalised to one row per week with channel columns.
  const engagementSeries = engagementData.map((d) => ({
    week: d.week,
    SMS: Number(d.SMS) || 0,
    'Voice SMS': Number(d.IVR) || 0,
    // The API emits this series as "ElevenLabs"; reading d.voiceAI —
    // a key it has never sent — kept the AI Voice line flat at zero.
    'AI Voice': Number(d.ElevenLabs ?? d.voiceAI) || 0,
    WhatsApp: Number(d.WhatsApp) || 0,
    total: Number(d.value) || 0,
  }));

  // Only render channels that actually carry data — the colour lookup is keyed
  // by channel, so dropping an empty series never repaints the survivors.
  const activeChannels = CHANNELS.filter((c) =>
    engagementSeries.some((row) => row[c.key] > 0)
  );
  // Some deployments return the weekly total without a per-channel split. Show
  // the aggregate rather than four empty stacks.
  const hasBreakdown = activeChannels.length > 0;
  const engagementLegend = hasBreakdown
    ? activeChannels
    : [{ key: 'Interactions', color: CHANNEL_COLORS.sms }];

  const peakWeek = engagementSeries.length
    ? Math.max(...engagementSeries.map((d) => d.total))
    : 0;

  const channelLegend = [
    { key: 'Delivered', color: NEUTRAL_MARK },
    { key: 'Engaged', color: CHANNEL_COLORS.sms },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5">
      <PageHeader
        title="Analytics"
        description="Real-time campaign delivery and engagement metrics"
      >
        {realtimeActive && (
          <span className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            Live
          </span>
        )}
        {lastUpdated && (
          <span className="hidden text-xs tabular-nums text-gray-400 sm:inline">
            Updated {lastUpdated.toLocaleTimeString()}
          </span>
        )}
        <RefreshButton busy={refreshing} onClick={() => fetchAll(true)} />
      </PageHeader>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-sm text-rose-700">
          <IconError size={16} className="flex-shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ id, ...stat }) => (
          <StatCard key={id} {...stat} loading={loading} />
        ))}
      </div>

      {/* Tabs */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-2">
          <div
            className="-mb-px flex gap-1 overflow-x-auto"
            role="tablist"
            aria-label="Analytics views"
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap border-b-2 px-3 py-3.5 text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                    isActive
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-500 hover:text-gray-900'
                  }`}
                >
                  {tab.name}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-5">
          {/* ── Engagement Trends ─────────────────────────────────────────── */}
          {activeTab === 'engagement' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                    Weekly engagement by channel
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Every outreach interaction, stacked by channel, over the last 4 weeks
                  </p>
                </div>
                {!loading && engagementSeries.length > 0 && (
                  <Legend items={engagementLegend} />
                )}
              </div>

              {loading ? (
                <LoadingBlock className="h-72" label="Loading engagement data…" />
              ) : engagementSeries.length === 0 ? (
                <EmptyState
                  className="h-72"
                  icon={IconChart}
                  title="No interactions recorded yet"
                  description="Send your first campaign and weekly engagement will start plotting here."
                />
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <span className="text-[28px] font-semibold leading-none tracking-[-0.02em] text-gray-900 tabular-nums">
                      {fmt(peakWeek)}
                    </span>
                    <span className="text-xs text-gray-500">peak weekly interactions</span>
                  </div>

                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={engagementSeries} barCategoryGap="28%" margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <CartesianGrid {...gridProps} />
                        <XAxis dataKey="week" {...axisProps} />
                        <YAxis {...yAxisProps} allowDecimals={false} />
                        <Tooltip
                          cursor={{ fill: '#f8f9fa' }}
                          content={<ChartTooltip showTotal />}
                        />
                        {hasBreakdown ? (
                          activeChannels.map((c, i) => (
                            <Bar
                              key={c.key}
                              dataKey={c.key}
                              stackId="engagement"
                              fill={c.color}
                              stroke={SURFACE}
                              strokeWidth={2}
                              maxBarSize={64}
                              isAnimationActive={false}
                              radius={i === activeChannels.length - 1 ? [4, 4, 0, 0] : 0}
                            />
                          ))
                        ) : (
                          <Bar
                            dataKey="total"
                            name="Interactions"
                            fill={CHANNEL_COLORS.sms}
                            maxBarSize={64}
                            isAnimationActive={false}
                            radius={[4, 4, 0, 0]}
                          />
                        )}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Channel Performance ───────────────────────────────────────── */}
          {activeTab === 'channel' && (
            <div>
              {loading ? (
                <LoadingBlock className="h-64" label="Loading channel data…" />
              ) : channelData.length === 0 ? (
                <EmptyState
                  className="h-64"
                  icon={IconChart}
                  title="No channel data yet"
                  description="Delivery and engagement per channel appear once a campaign has run."
                />
              ) : (
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                  {/* Delivered vs engaged — neutral reference bar, accent focus bar */}
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                        Delivered vs engaged
                      </h3>
                      <Legend items={channelLegend} />
                    </div>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={channelData} barGap={4} barCategoryGap="30%" margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                          <CartesianGrid {...gridProps} />
                          <XAxis dataKey="channel" {...axisProps} />
                          <YAxis {...yAxisProps} allowDecimals={false} />
                          <Tooltip cursor={{ fill: '#f8f9fa' }} content={<ChartTooltip />} />
                          <Bar dataKey="delivered" name="Delivered" fill={NEUTRAL_MARK} maxBarSize={28} isAnimationActive={false} radius={[4, 4, 0, 0]} />
                          <Bar dataKey="engaged" name="Engaged" fill={CHANNEL_COLORS.sms} maxBarSize={28} isAnimationActive={false} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Engagement rate per channel */}
                  <div className="space-y-5">
                    <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                      Engagement rate by channel
                    </h3>
                    <div className="space-y-4">
                      {channelData.map((ch) => (
                        <MeterRow
                          key={ch.channel}
                          label={ch.channel}
                          value={ch.percentage}
                          hint={`${fmt(ch.delivered)} delivered`}
                          valueLabel={`${ch.percentage}%`}
                        />
                      ))}
                    </div>

                    {summary && (
                      <div className="grid grid-cols-1 gap-3 border-t border-gray-100 pt-5 sm:grid-cols-2">
                        {[
                          {
                            label: 'Voice SMS answered',
                            value: voiceRate,
                            detail: `${fmt(voiceAnswered)} of ${fmt(voiceCalls)} calls`,
                            tone: 'voice',
                            color: CHANNEL_COLORS.voiceSms,
                          },
                          {
                            label: 'AI Voice answered',
                            value: aiRate,
                            detail: `${fmt(aiAnswered)} of ${fmt(aiCalls)} calls`,
                            tone: 'ai',
                            color: CHANNEL_COLORS.aiVoice,
                          },
                        ].map((row) => (
                          <div
                            key={row.label}
                            className="rounded-lg border border-gray-200 px-4 py-3.5"
                          >
                            <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                              <span
                                aria-hidden="true"
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ backgroundColor: row.color }}
                              />
                              {row.label}
                            </p>
                            <p className="mt-1.5 text-[22px] font-semibold leading-none tracking-[-0.01em] text-gray-900 tabular-nums">
                              {pct(row.value)}
                            </p>
                            <Meter value={row.value} tone={row.tone} size="sm" className="mt-3" label={row.label} />
                            <p className="mt-2 text-xs text-gray-400">{row.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── SMS & Voice ───────────────────────────────────────────────── */}
          {activeTab === 'mnotify' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                  Delivery breakdown
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Per-vendor SMS delivery and voice call outcomes
                </p>
              </div>

              {loading ? (
                <LoadingBlock className="h-64" label="Loading delivery data…" />
              ) : !mnotifyStats && !summary ? (
                <EmptyState
                  className="h-64"
                  icon={IconSMS}
                  title="No delivery data yet"
                  description="Send an SMS or voice campaign and per-channel outcomes will land here."
                />
              ) : (
                <div className="space-y-8">
                  {/* SMS */}
                  <section className="space-y-4">
                    <SeriesLabel color={CHANNEL_COLORS.sms}>SMS</SeriesLabel>
                    <MetricGrid className="grid-cols-2 lg:grid-cols-4">
                      <MetricTile
                        label="Total sent"
                        value={fmt(mnotifyStats?.sms?.total_sent)}
                        hint={`across ${fmt(mnotifyStats?.sms?.total_campaigns)} campaign${
                          mnotifyStats?.sms?.total_campaigns !== 1 ? 's' : ''
                        }`}
                      />
                      <MetricTile
                        label="Delivered"
                        value={fmt(mnotifyStats?.sms?.delivered)}
                        hint={`${smsRate}% delivery rate`}
                        dot
                        statusTone="good"
                      />
                      <MetricTile
                        label="Pending"
                        value={fmt(mnotifyStats?.sms?.pending)}
                        hint="awaiting a receipt"
                        dot
                        statusTone="warning"
                      />
                      <MetricTile
                        label="Failed"
                        value={fmt(mnotifyStats?.sms?.failed)}
                        hint="rejected by the carrier"
                        dot
                        statusTone="critical"
                      />
                    </MetricGrid>
                    {mnotifyStats?.sms?.total_sent > 0 && (
                      <MeterRow
                        label="Delivery rate"
                        value={smsRate}
                        valueLabel={`${smsRate}%`}
                        hint={`${fmt(mnotifyStats.sms.delivered)} of ${fmt(mnotifyStats.sms.total_sent)}`}
                      />
                    )}
                  </section>

                  {/* Voice SMS */}
                  <section className="space-y-4">
                    <SeriesLabel color={CHANNEL_COLORS.voiceSms}>Voice SMS</SeriesLabel>
                    <MetricGrid className="grid-cols-2 lg:grid-cols-4">
                      <MetricTile
                        label="Total calls"
                        value={fmt(voiceCalls)}
                        hint={`across ${fmt(mnotifyStats?.voice?.total_campaigns)} campaign${
                          mnotifyStats?.voice?.total_campaigns !== 1 ? 's' : ''
                        }`}
                      />
                      <MetricTile
                        label="Answered"
                        value={fmt(voiceAnswered)}
                        hint={`${voiceRate}% answer rate`}
                        dot
                        statusTone="good"
                      />
                      <MetricTile
                        label="Unanswered"
                        value={fmt(mnotifyStats?.voice?.unanswered ?? 0)}
                        hint="no pick-up"
                      />
                      {/* Calls whose delivery report has not landed yet are not
                          misses. They used to be folded into Unanswered as
                          (total - answered), so a healthy campaign read as a
                          failure until mNotify reported — minutes to hours. */}
                      <MetricTile
                        label="Awaiting report"
                        value={fmt(mnotifyStats?.voice?.pending ?? 0)}
                        hint="result not in yet"
                      />
                      <MetricTile
                        label="Avg duration"
                        value={
                          mnotifyStats?.voice?.avg_duration_seconds > 0
                            ? `${Math.round(mnotifyStats.voice.avg_duration_seconds)}s`
                            : '—'
                        }
                        hint="per answered call"
                      />
                    </MetricGrid>
                    {voiceCalls > 0 && (
                      <MeterRow
                        label="Answer rate"
                        value={voiceRate}
                        valueLabel={`${voiceRate}%`}
                        hint={`${fmt(voiceAnswered)} of ${fmt(voiceCalls)}`}
                        tone="voice"
                      />
                    )}
                  </section>

                  {/* AI Voice */}
                  {summary && (
                    <section className="space-y-4">
                      <SeriesLabel color={CHANNEL_COLORS.aiVoice}>AI Voice</SeriesLabel>
                      <MetricGrid className="grid-cols-2 lg:grid-cols-4">
                        <MetricTile label="Total calls" value={fmt(aiCalls)} hint="conversations placed" />
                        <MetricTile
                          label="Answered"
                          value={fmt(aiAnswered)}
                          hint={`${aiRate}% answer rate`}
                          dot
                          statusTone="good"
                        />
                        <MetricTile
                          label="Unanswered"
                          value={fmt(Math.max(0, aiCalls - aiAnswered))}
                          hint="no pick-up"
                        />
                        <MetricTile label="Answer rate" value={pct(aiRate)} hint="of all placed calls" />
                      </MetricGrid>
                      {aiCalls > 0 && (
                        <MeterRow
                          label="Answer rate"
                          value={aiRate}
                          valueLabel={`${aiRate}%`}
                          hint={`${fmt(aiAnswered)} of ${fmt(aiCalls)}`}
                          tone="ai"
                        />
                      )}
                    </section>
                  )}

                  {/* Combined totals */}
                  {(mnotifyStats || summary) && (
                    <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3 rounded-xl border border-gray-200 bg-gray-50/60 px-5 py-4">
                      <div>
                        <p className="text-xs text-gray-500">Total outreach events</p>
                        <p className="mt-1 text-lg font-semibold tabular-nums tracking-[-0.01em] text-gray-900">
                          {fmt((mnotifyStats?.sms?.total_sent || 0) + voiceCalls + aiCalls)}
                        </p>
                      </div>
                      {[
                        { label: 'SMS messages', value: mnotifyStats?.sms?.total_sent || 0, color: CHANNEL_COLORS.sms },
                        { label: 'Voice SMS calls', value: voiceCalls, color: CHANNEL_COLORS.voiceSms },
                        { label: 'AI Voice calls', value: aiCalls, color: CHANNEL_COLORS.aiVoice },
                      ].map((row) => (
                        <div key={row.label}>
                          <p className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span
                              aria-hidden="true"
                              className="h-1.5 w-1.5 rounded-full"
                              style={{ backgroundColor: row.color }}
                            />
                            {row.label}
                          </p>
                          <p className="mt-1 text-lg font-semibold tabular-nums tracking-[-0.01em] text-gray-900">
                            {fmt(row.value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Feedback ──────────────────────────────────────────────────── */}
          {activeTab === 'feedback' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                  Patient feedback
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Post-call and post-SMS responses collected from patients
                </p>
              </div>

              {loading ? (
                <LoadingBlock className="h-64" label="Loading feedback…" />
              ) : !feedbackData || feedbackData.total_feedback === 0 ? (
                <EmptyState
                  className="h-64"
                  icon={IconStar}
                  title="No feedback recorded yet"
                  description="Feedback arrives when patients answer a post-call survey or reply to an SMS keyword. Point the /feedback/submit webhook at this app to start tracking."
                />
              ) : (
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                  <div className="space-y-5">
                    {/* Average rating */}
                    <div className="rounded-xl border border-gray-200 px-5 py-4">
                      <p className="text-xs font-medium text-gray-500">Average rating</p>
                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-[34px] font-semibold leading-none tracking-[-0.02em] text-gray-900 tabular-nums">
                          {feedbackData.avg_rating.toFixed(1)}
                        </span>
                        <span>
                          <StarRating rating={feedbackData.avg_rating} />
                          <span className="mt-1 block text-xs text-gray-400">
                            from {fmt(feedbackData.total_feedback)} responses
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Sentiment split — status colour is always paired with a glyph */}
                    <MetricGrid className="grid-cols-2">
                      <div className="bg-white px-4 py-3.5">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          <IconSuccess
                            size={14}
                            style={{ color: STATUS_COLORS.good }}
                            aria-hidden="true"
                          />
                          Positive (4–5★)
                        </p>
                        <p className="mt-1.5 text-[20px] font-semibold leading-none tabular-nums tracking-[-0.01em] text-gray-900">
                          {feedbackData.positive_pct}%
                        </p>
                      </div>
                      <div className="bg-white px-4 py-3.5">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                          <IconError
                            size={14}
                            style={{ color: STATUS_COLORS.critical }}
                            aria-hidden="true"
                          />
                          Negative (1–2★)
                        </p>
                        <p className="mt-1.5 text-[20px] font-semibold leading-none tabular-nums tracking-[-0.01em] text-gray-900">
                          {feedbackData.negative_pct}%
                        </p>
                      </div>
                    </MetricGrid>

                    {/* Rating distribution */}
                    <div>
                      <p className="mb-3 text-xs font-medium text-gray-500">Rating distribution</p>
                      <div className="space-y-3">
                        {[5, 4, 3, 2, 1].map((r) => {
                          const count = feedbackData.rating_distribution[String(r)] || 0;
                          const share =
                            feedbackData.total_feedback > 0
                              ? Math.round((count / feedbackData.total_feedback) * 100)
                              : 0;
                          return (
                            <div key={r} className="flex items-center gap-3">
                              <span className="w-6 flex-shrink-0 text-xs font-medium tabular-nums text-gray-500">
                                {r}★
                              </span>
                              <Meter value={share} label={`${r} star`} />
                              <span className="w-10 flex-shrink-0 text-right text-xs tabular-nums text-gray-400">
                                {fmt(count)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Recent feedback */}
                  <div>
                    <p className="mb-3 text-xs font-medium text-gray-500">Recent feedback</p>
                    <div className="max-h-96 space-y-2.5 overflow-y-auto pr-1">
                      {feedbackData.recent_feedback.length === 0 ? (
                        <p className="py-4 text-center text-sm text-gray-400">
                          No written feedback yet
                        </p>
                      ) : (
                        feedbackData.recent_feedback.map((fb, i) => (
                          <div key={i} className="rounded-lg border border-gray-200 px-4 py-3">
                            <div className="mb-1.5 flex items-center justify-between gap-3">
                              <StarRating rating={fb.rating || 0} />
                              <div className="flex items-center gap-2 text-xs text-gray-400">
                                <span>{CHANNEL_LABELS[fb.channel] || fb.channel || 'Unknown'}</span>
                                {fb.at && (
                                  <span className="tabular-nums">
                                    {new Date(fb.at).toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: 'short',
                                    })}
                                  </span>
                                )}
                              </div>
                            </div>
                            {fb.text ? (
                              <p className="text-[13px] leading-relaxed text-gray-700">
                                &ldquo;{fb.text}&rdquo;
                              </p>
                            ) : (
                              <p className="text-[13px] italic text-gray-400">No text provided</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Diagnoses ─────────────────────────────────────────────────── */}
          {activeTab === 'diagnosis' && (
            <div className="space-y-5">
              <div>
                <h3 className="text-[15px] font-semibold tracking-[-0.01em] text-gray-900">
                  Patient diagnoses
                </h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Distribution of recorded diagnoses across your roster
                </p>
              </div>

              {diagnosisLoading ? (
                <LoadingBlock className="h-64" label="Loading diagnoses…" />
              ) : diagnosisData.length === 0 ? (
                <EmptyState
                  className="h-64"
                  icon={IconDiagnosis}
                  title="No diagnosis data yet"
                  description="Add patients with a diagnosis in Patient Management to see the breakdown here."
                />
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const total = diagnosisData.reduce((s, d) => s + d.count, 0);
                    return diagnosisData.map(({ diagnosis, count }, i) => {
                      const share = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <MeterRow
                          key={diagnosis}
                          rank={i + 1}
                          label={diagnosis}
                          value={share}
                          hint={`${count} patient${count !== 1 ? 's' : ''}`}
                          valueLabel={`${share}%`}
                        />
                      );
                    });
                  })()}
                  <p className="border-t border-gray-100 pt-3 text-xs text-gray-400">
                    {fmt(diagnosisData.reduce((s, d) => s + d.count, 0))} patients with a recorded
                    diagnosis
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data sources */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-xl border border-gray-200 px-5 py-3.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500">
          <IconInfo size={14} aria-hidden="true" />
          Data sources
        </span>
        {[
          { label: 'AI Voice — conversation API', color: CHANNEL_COLORS.aiVoice },
          { label: 'Voice SMS — delivery webhook', color: CHANNEL_COLORS.voiceSms },
          { label: 'Campaign database — interactions & feedback', color: CHANNEL_COLORS.sms },
        ].map((src) => (
          <span key={src.label} className="inline-flex items-center gap-1.5 text-xs text-gray-400">
            <span
              aria-hidden="true"
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: src.color }}
            />
            {src.label}
          </span>
        ))}
        {realtimeActive && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
            <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Real-time updates active
          </span>
        )}
      </div>
    </div>
  );
};

export default Analytics;
